/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Image,
    Text,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {REMOVE_FILTER} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {getUrl} from '../../../utils/cloudinary-utils'
import {useStoreContext} from '../../../contexts/store'

export const getSelectedFilters = (allfilters, selectedFilterValues) => {
    const priceFilterValues = allfilters?.find(
        (filter) => filter.attributeId?.toLowerCase()?.indexOf('price') !== -1
    )
    const filterValuesByAttribute = new Map(
        allfilters?.map((filter) => [
            filter.attributeId,
            new Set(filter?.values?.map((value) => value.value))
        ])
    )
    const selectedFilters = []
    for (const key in selectedFilterValues) {
        const selectedValue = selectedFilterValues[key]
        const filters = Array.isArray(selectedValue)
            ? selectedValue
            : String(selectedValue || '')
                  .split('|')
                  .filter(Boolean)
        filters?.forEach((filter) => {
            const selected = {
                uiLabel:
                    key?.toLowerCase()?.indexOf('price') !== -1
                        ? priceFilterValues?.values?.find(
                              (priceFilter) => priceFilter.value === filter
                          )?.label
                        : filter,
                value: key,
                apiLabel: filter
            }
            const validValues = filterValuesByAttribute.get(selected.value)
            const mismatch =
                validValues &&
                !validValues.has(selected.apiLabel) &&
                !(
                    selected.value === 'in_stock_at' &&
                    [...validValues].some((v) => v?.includes(selected.apiLabel))
                )
            if (selected.value !== 'htype' && selected.value !== 'cgid' && !mismatch) {
                // don't show these to user
                selectedFilters.push(selected)
            }
        })
    }
    return selectedFilters
}

const SelectedRefinements = ({toggleFilter, selectedFilterValues, filters, handleReset}) => {
    const {formatMessage} = useIntl()
    const productListStyles = useStyleConfig('ProductListStyles')
    const selectedFilters = useMemo(
        () =>
            getSelectedFilters(filters, selectedFilterValues)
                ?.sort((a, b) =>
                    a?.value === 'c_RingSize' && b?.value === 'c_RingSize'
                        ? Number(a.uiLabel) - Number(b.uiLabel)
                        : 0
                )
                ?.sort((a, b) => {
                    const priority = ['c_availableInStores', 'in_stock_at']
                    const aPriority = priority.includes(a.value)
                        ? priority.indexOf(a.value)
                        : Infinity
                    const bPriority = priority.includes(b.value)
                        ? priority.indexOf(b.value)
                        : Infinity
                    return aPriority - bPriority
                }),
        [filters, selectedFilterValues]
    )
    const {store: selectedStore} = useStoreContext()
    const scrollRef = useRef(null)

    return (
        <Box
            display="flex"
            alignItems="normal"
            marginTop={{lg: '0'}}
            marginLeft={{lg: '60px'}}
            marginRight={{lg: '60px'}}
        >
            <Box
                ref={scrollRef}
                display="flex"
                alignItems="center"
                sx={{
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    '&::-webkit-scrollbar': {
                        height: '2px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'gray.800',
                        borderRadius: '2px',
                        display: 'block'
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'gray.400',
                        borderRadius: '2px',
                        display: 'block'
                    },
                    userSelect: 'none',
                    scrollBehavior: 'smooth'
                }}
                flexWrap="nowrap"
                data-testid="sf-selected-refinements"
            >
                {selectedFilters?.map((filter, idx) => (
                    <Box marginLeft={0} marginRight="10px" key={idx}>
                        <Button
                            {...productListStyles.body.refineSortWrapper.selectedRefinements.button}
                            rightIcon={
                                <Image
                                    alt="Cross Icon"
                                    marginLeft="0.375rem"
                                    width="10px"
                                    height="9px"
                                    src={getUrl('UIImages/images/crossed-icon.svg')}
                                />
                            }
                            onClick={() => {
                                toggleFilter({value: filter.apiLabel}, filter.value, true)
                            }}
                            borderColor="transparent"
                            data-refinement={filter.value}
                            paddingTop="10px"
                            paddingBottom="10px"
                            data-label={filter.uiLabel}
                            cursor="pointer"
                            aria-label={formatMessage(REMOVE_FILTER, {label: filter.uiLabel})}
                        >
                            {/** NOTE: We need to display the selected store only if it exists in context. **/}
                            {filter.value === 'c_availableInStores' ||
                            (filter.value === 'in_stock_at' && selectedStore) ? (
                                <>
                                    <Image
                                        className="store-locator-svg"
                                        width="16px"
                                        height="18px"
                                        src={getUrl('UIImages/images/pin-black.svg')}
                                        alt="Store Locator"
                                        marginRight={{base: '10px', lg: '5px'}}
                                    />
                                    <Text
                                        fontWeight="300"
                                        color="black"
                                        lineHeight="initial"
                                        marginRight="5px"
                                        letterSpacing="0.08px"
                                        fontSize="16px"
                                        display={{base: 'none', lg: 'inline'}}
                                    >
                                        <FormattedMessage
                                            id="available.at"
                                            defaultMessage="Available for pick up at"
                                        />
                                    </Text>
                                    <Text
                                        fontWeight="300"
                                        color="teal.500"
                                        lineHeight="initial"
                                        textDecoration="underline"
                                        textUnderlineOffset="4px"
                                        letterSpacing="0.08px"
                                        fontSize="16px"
                                        display="inline"
                                    >
                                        {(() => {
                                            const label = selectedStore?.name
                                            return label
                                                ?.toLowerCase()
                                                .replace(/\b\w/g, (char) => char.toUpperCase())
                                        })()}
                                    </Text>
                                </>
                            ) : (
                                <Text lineHeight="initial">{filter.uiLabel}</Text>
                            )}
                        </Button>
                    </Box>
                ))}
            </Box>
            {selectedFilters?.length > 0 && (
                <Box marginLeft="10px">
                    <Button
                        padding="0.7rem 0rem 0.5rem 0.5rem"
                        variant="link"
                        fontSize="14px"
                        color="black"
                        fontWeight="300"
                        textDecoration="underline"
                        textUnderlineOffset="4px"
                        onClick={() => {
                            handleReset()
                        }}
                        aria-label={formatMessage({
                            id: 'selected_refinements.action.assistive_msg.clear_all',
                            defaultMessage: 'Clear all filters'
                        })}
                    >
                        <FormattedMessage
                            defaultMessage="Clear All"
                            id="selected_refinements.action.clear_all"
                        />
                    </Button>
                </Box>
            )}
        </Box>
    )
}

SelectedRefinements.propTypes = {
    filters: PropTypes.array,
    selectedFilterValues: PropTypes.object,
    toggleFilter: PropTypes.func,
    handleReset: PropTypes.func
}

export default SelectedRefinements
